import { lazy, Suspense } from 'react';
import { bt } from '../../i18n';
import { parseHeadingMarkup, type RichtextField, type RichtextValue } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { toRichtext } from '../richtext/sanitizeRichtext';

// Code-split Tiptap + ProseMirror (the heaviest editor dep, editing-only) off the cold-load path
// (build-plan §9.6) — mounted on demand when a richtext field renders.
const TiptapEditor = lazy(() => import('../richtext/TiptapEditor'));

/**
 * Richtext field (build-plan §6.3/§6.4): a Tiptap editor whose `onUpdate` sanitizes with the SHARED
 * RICHTEXT_ALLOW policy at WRITE time, storing a clean `{format:'html', html}`. The storefront SSR path
 * re-sanitizes the same way at render (ui-react `sanitizeRichtextHtml`) — defense at both ends.
 */
export function RichtextControl({
  field,
  value,
  onChange,
}: {
  field: RichtextField;
  value: RichtextValue | undefined;
  onChange: (next: RichtextValue) => void;
}) {
  // A field that became rich text (e.g. a section heading) can still hold a LEGACY plain STRING with
  // `**bold**`/`*italic*` markup from before the change. `value` is typed RichtextValue, but such a string
  // arrives at runtime — coerce it to editor HTML (bold/italic preserved) so the field opens WITH its text
  // instead of blank. On the seller's first edit it is re-saved as a clean `{format:'html', html}`.
  const raw = value as RichtextValue | string | undefined;
  const html =
    raw && typeof raw === 'object' && raw.format === 'html'
      ? raw.html
      : typeof raw === 'string'
        ? parseHeadingMarkup(raw)
        : '';
  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span">
      <Suspense
        fallback={
          <div className="bld-rte__loading">{bt('chrome.rte.loading', 'Loading editor…')}</div>
        }
      >
        <TiptapEditor html={html} onChange={(clean) => onChange(toRichtext(clean))} />
      </Suspense>
    </FieldRow>
  );
}
